/**
 * File: pushClientService.ts
 * Path: apps/admin/src/shared/services/pushClientService.ts
 * Description: Switching notifications on and off for this browser, and
 * keeping its push token fresh.
 *
 * Tokens go stale (iPhones especially drop them quietly), so every app open
 * re-registers a browser that had notifications on (`refreshPush`). The
 * device id is remembered in this browser so a new token replaces its old
 * entry instead of adding another.
 */

import { FunctionsService, getFirebaseApp } from "@idk/config";
import { deleteToken, getMessaging, getToken, isSupported } from "firebase/messaging";
import { deviceLabel, readBrowserFacts } from "../utils/deviceUtils";
import { SERVICE_WORKER_URL } from "../utils/registerServiceWorkerUtils";

const DEVICE_KEY = "idk.pushDeviceId";

const remember = {
  get: (): string | null => {
    try {
      return window.localStorage.getItem(DEVICE_KEY);
    } catch {
      return null;
    }
  },
  set: (id: string | null) => {
    try {
      if (id) window.localStorage.setItem(DEVICE_KEY, id);
      else window.localStorage.removeItem(DEVICE_KEY);
    } catch {
      // Private mode: the device still works, it just can't be recognised next time.
    }
  },
};

/** This browser's device id, if notifications were switched on here. */
export const thisDeviceId = (): string | null => remember.get();

const messagingToken = async (): Promise<string> => {
  if (!(await isSupported())) throw new Error("This browser can't receive notifications.");
  const registration = await navigator.serviceWorker.register(SERVICE_WORKER_URL, { updateViaCache: "none" });
  await navigator.serviceWorker.ready;
  return getToken(getMessaging(getFirebaseApp()), {
    vapidKey: import.meta.env.VITE_FIREBASE_VAPID_KEY,
    serviceWorkerRegistration: registration,
  });
};

const register = async (token: string): Promise<string> => {
  const facts = readBrowserFacts();
  const { deviceId } = await FunctionsService.call<
    { token: string; label: string; installed: boolean; previousDeviceId?: string },
    { deviceId: string }
  >("registerPushDevice", {
    token,
    label: deviceLabel(facts),
    installed: facts.installed,
    ...(remember.get() ? { previousDeviceId: remember.get() as string } : {}),
  });
  remember.set(deviceId);
  return deviceId;
};

const friendly = (error: unknown): Error => {
  const code = (error as { code?: string })?.code ?? "";
  if (code.includes("permission-blocked") || code.includes("permission-default")) {
    return new Error("Notifications are blocked for this site. Allow them in your browser's settings, then try again.");
  }
  if (code.includes("failed-service-worker-registration")) {
    return new Error("The app couldn't start its background worker. Reload the page and try again.");
  }
  return error instanceof Error ? error : new Error("Couldn't switch notifications on.");
};

/**
 * Asks permission and registers this browser. Call straight from a tap:
 * iPhones only show the permission prompt in direct response to one.
 */
export const enablePush = async (): Promise<string> => {
  const permission = await Notification.requestPermission();
  if (permission !== "granted") {
    throw new Error(
      permission === "denied"
        ? "Notifications are blocked for this site. Allow them in your browser's settings, then try again."
        : "Notifications weren't allowed. Tap the button again and choose Allow.",
    );
  }
  try {
    return await register(await messagingToken());
  } catch (error) {
    throw friendly(error);
  }
};

/** On app open: re-registers a browser that had notifications on, so its token stays current. */
export const refreshPush = async (): Promise<void> => {
  if (!remember.get() || typeof Notification === "undefined" || Notification.permission !== "granted") return;
  if (!import.meta.env.VITE_FIREBASE_VAPID_KEY) return;
  try {
    await register(await messagingToken());
  } catch (error) {
    console.warn("Couldn't refresh this device's notifications", error);
  }
};

/**
 * Drops this browser's push subscription. Firebase's deleteToken goes looking
 * for its own /firebase-messaging-sw.js unless it has already been handed our
 * worker, and that file doesn't exist here (the server answers with the app
 * page, which the browser rejects with a MIME type error). So it is always
 * handed /sw.js first, and without permission the subscription is dropped
 * directly.
 */
const forgetToken = async (): Promise<void> => {
  if (!(await isSupported())) return;
  const registration = await navigator.serviceWorker.getRegistration();
  if (!registration) return;
  const vapidKey = import.meta.env.VITE_FIREBASE_VAPID_KEY;
  if (Notification.permission === "granted" && vapidKey) {
    const messaging = getMessaging(getFirebaseApp());
    await getToken(messaging, { vapidKey, serviceWorkerRegistration: registration });
    await deleteToken(messaging);
    return;
  }
  const subscription = await registration.pushManager.getSubscription();
  await subscription?.unsubscribe();
};

/** Stops notifications on this browser: forgets the token here and removes the device. */
export const disablePush = async (removeDevice: (deviceId: string) => Promise<void>): Promise<void> => {
  const id = remember.get();
  try {
    await forgetToken();
  } catch {
    // Already gone; removing the device below is what stops the sends.
  }
  if (id) await removeDevice(id);
  remember.set(null);
};
